'use client'

import type { ProductShowcaseBlock as ProductShowcaseBlockProps } from '@/payload-types'
import React, { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import type { Media } from '@/payload-types'
import { getBlockPadding } from '@/utilities/getBlockPadding'
import { RightArrowIcon } from '@/components/icons/RightArrowIcon'
import { ToggleButton } from '@/components/ui/toggle-button'
import { cn } from '@/utilities/ui'

const LOGO_SIZES: Record<string, { width: number; height: number; className: string }> = {
  '/assets-rebrand/logos/civo-public-black.svg': { width: 2826, height: 666, className: 'h-5 md:h-[35px]' },
  '/assets-rebrand/logos/civo-private-black.svg': { width: 3092, height: 666, className: 'h-5 md:h-[35px]' },
  '/assets-rebrand/logos/civo-ai-black.svg': { width: 1646, height: 505, className: 'h-[15px] md:h-[26px]' },
  '/assets-rebrand/logos/konstruct-by-civo-logo-black.svg': { width: 1677, height: 295, className: 'h-[14px] md:h-[25px]' },
}

type Props = {
  className?: string
  disableInnerContainer?: boolean
} & ProductShowcaseBlockProps

export const ProductShowcaseBlock: React.FC<Props> = ({
  items,
  disableInnerContainer,
  paddingTop,
  paddingBottom,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })

  if (!items || items.length === 0) {
    return null
  }

  return (
    <div className={`w-full bg-white ${paddingClasses}`}>
      <div className={disableInnerContainer ? '' : 'container mx-auto px-7 md:px-9 xl:px-16'}>
        <div className="max-w-[1296px] mx-auto flex flex-col gap-2 md:gap-0">
          {items.map((item, index) => {
            // Auto-generate number based on index (1-based, zero-padded)
            const displayNumber = String(index + 1).padStart(2, '0')
            const itemId = item.id || `item-${index}`

            // Determine if we're showing logo or text title
            const titleType = item.titleType || 'logo'
            const isLogoTitle = titleType === 'logo'

            // Handle logo URL
            const logo = item.logo as Media | string | undefined
            let logoUrl = ''

            if (typeof logo === 'string') {
              logoUrl = logo
            } else if (logo && typeof logo === 'object' && 'url' in logo) {
              logoUrl = (logo as Media).url || ''
            }

            const logoAlt = item.title || `Product ${displayNumber}`
            const hasValidLogo = logoUrl && logoUrl.trim() !== ''
            const hasUrl = item.url && item.url.trim() !== ''

            const rowContent = (
              <>
                {/* Number - hidden on mobile */}
                <div className="hidden lg:flex relative z-10 body-copy-medium text-black shrink-0 size-[88px] items-center justify-center">
                  {displayNumber}
                </div>

                {/* Title - Logo or Text (flex on mobile, fixed width on desktop for alignment) */}
                <div className="relative z-10 flex-1 md:flex-none md:w-[280px] lg:w-[400px] xl:w-[500px] md:shrink-0 flex items-center justify-start min-w-0 overflow-hidden">
                  {isLogoTitle && hasValidLogo ? (
                    <div className="shrink-0 max-h-8 md:max-h-[55px]">
                      <Image
                        src={logoUrl}
                        alt={logoAlt}
                        width={LOGO_SIZES[logoUrl]?.width ?? 200}
                        height={LOGO_SIZES[logoUrl]?.height ?? 55}
                        className={cn(
                          'w-auto object-contain object-left',
                          LOGO_SIZES[logoUrl]?.className ?? 'h-auto max-h-8 md:max-h-[55px]',
                        )}
                      />
                    </div>
                  ) : !isLogoTitle && item.title ? (
                    <h3 className="display-x-small md:display-small text-black [word-break:break-word]">
                      {item.title}
                    </h3>
                  ) : null}
                </div>

                {/* Description - hidden on mobile, expands to fill available space */}
                <div className="relative z-10 hidden md:block flex-1">
                  <p className="body-copy-medium text-black/70 leading-relaxed">{item.text}</p>
                </div>

                {/* Arrow Button - only shown when hasUrl */}
                {hasUrl && (
                  <div className="relative z-10 size-[45px] md:size-[88px] flex items-center justify-center shrink-0">
                    <div className="size-[45px] md:size-[50px] rounded-full bg-civo-lime flex items-center justify-center transition-colors duration-200">
                      <RightArrowIcon />
                    </div>
                  </div>
                )}

                {/* Toggle Button - only shown on mobile when no URL */}
                {!hasUrl && (
                  <div className="relative z-10 md:hidden flex items-center justify-center shrink-0">
                    <ToggleButton
                      isExpanded={expandedId === itemId}
                      selectedColor="black"
                      unselectedColor="lime"
                    />
                  </div>
                )}
              </>
            )

            // Mobile expandable description (for items without URL)
            const expandableContent = !hasUrl && (
              <div
                className="relative z-10 md:hidden grid transition-[grid-template-rows] duration-300 ease-in-out"
                style={{ gridTemplateRows: expandedId === itemId ? '1fr' : '0fr' }}
              >
                <div className="overflow-hidden">
                  <p className="body-copy-medium text-black/70 leading-relaxed pt-3 pb-2">
                    {item.text}
                  </p>
                </div>
              </div>
            )

            const baseClasses =
              'relative px-6 md:px-0 py-4 md:py-7 border md:border-0 border-civo-cloud rounded-[16px] md:rounded-none md:border-t md:last:border-b md:border-civo-cloud overflow-hidden before:absolute before:inset-0 before:bg-civo-cloud before:translate-y-full before:transition-transform before:duration-300 before:ease-in-out hover:before:translate-y-0'

            return hasUrl ? (
              <Link
                key={itemId}
                href={item.url || '#'}
                className={`${baseClasses} flex items-center gap-3 md:gap-6`}
              >
                {rowContent}
              </Link>
            ) : (
              <div
                key={itemId}
                className={`${baseClasses} md:flex md:items-center md:gap-6 md:cursor-default cursor-pointer`}
                onClick={() => toggleExpand(itemId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleExpand(itemId)
                  }
                }}
              >
                {/* Main row - wrapper needed on mobile to keep expandable outside flex gap */}
                <div className="flex items-center gap-3 md:contents">
                  {rowContent}
                </div>
                {/* Expandable content - outside flex row so no gap affects it */}
                {expandableContent}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
